import { Field } from '@base-ui/react/field';
import { Select } from '@base-ui/react/select';
import { cn } from '@foundation/utils/cn';
import { ComponentPropsWithRef, ReactNode } from 'react';

export type HeadlessSelectOption<Value> = {
  readonly value: Value;
  readonly label: ReactNode;
  readonly disabled?: boolean;
};

export type HeadlessSelectStyles = {
  readonly root?: string;
  readonly label?: string;
  readonly requiredIndicator?: string;
  readonly trigger?: string;
  readonly value?: string;
  readonly icon?: string;
  readonly popup?: string;
  readonly item?: string;
  readonly itemIndicator?: string;
  readonly helperText?: string;
  readonly error?: string;
};

export type HeadlessSelectProps<Value> = {
  readonly options: ReadonlyArray<HeadlessSelectOption<Value>>;
  readonly placeholder?: ReactNode;
  readonly label?: ReactNode;
  readonly error?: ReactNode;
  readonly helperText?: ReactNode;
  readonly value?: Value | null;
  readonly defaultValue?: Value | null;
  readonly onValueChange?: (value: Value) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly showRequired?: boolean;
  readonly name?: string;
  readonly autoComplete?: string;
  readonly renderIcon?: () => ReactNode;
  readonly renderItemIndicator?: () => ReactNode;
  readonly styles?: HeadlessSelectStyles;
  readonly 'data-testid'?: string;
  /**
   * Renders a real `<select>`/`<option>` element instead of Base UI's Select.
   * Useful on mobile for the OS's native picker UI. Option `label`s must be
   * plain text in this mode — `<option>` has no rich content model.
   */
  readonly useNative?: boolean;
};

export function HeadlessSelect<Value>({
  options,
  placeholder,
  label,
  error,
  helperText,
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  showRequired = false,
  name,
  autoComplete,
  renderIcon,
  renderItemIndicator,
  styles = {},
  'data-testid': dataTestId,
  useNative = true,
}: HeadlessSelectProps<Value>) {
  const fieldError = error ? (
    <Field.Error
      match
      className={styles.error}
    >
      {error}
    </Field.Error>
  ) : (
    helperText && (
      <Field.Description className={styles.helperText}>
        {helperText}
      </Field.Description>
    )
  );

  if (useNative) {
    // Native <option> values must be strings, so options are addressed by their
    // index in `options` rather than serializing the (possibly non-string) Value.
    const indexOf = (option: Value | null | undefined) =>
      option == null
        ? ''
        : String(options.findIndex((item) => item.value === option));
    const isControlled = value !== undefined;

    return (
      <Field.Root
        className={cn('flex w-full flex-col gap-1', styles.root)}
        disabled={disabled}
        invalid={Boolean(error)}
      >
        <div className="relative flex items-center">
          {/* Field.Control types itself for <select>'s sibling <input>; render swaps the tag, so
              the <select>-shaped props below need a bridging cast to satisfy that mismatch. */}
          <Field.Control
            render={<select />}
            {...({
              required,
              disabled,
              name,
              autoComplete,
              'data-testid': dataTestId,
              value: isControlled ? indexOf(value) : undefined,
              defaultValue:
                defaultValue !== undefined ? indexOf(defaultValue) : undefined,
              onChange: (event: { target: { value: string } }) => {
                const option = options[Number(event.target.value)];
                if (option) {
                  onValueChange?.(option.value);
                }
              },
              className: cn('w-full appearance-none', styles.trigger),
            } as unknown as ComponentPropsWithRef<'input'>)}
          >
            {placeholder && (
              <option
                value=""
                disabled
                hidden
              >
                {placeholder}
              </option>
            )}
            {options.map((option, index) => (
              <option
                key={String(option.value)}
                value={index}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </Field.Control>

          <span
            aria-hidden="true"
            className={cn('pointer-events-none', styles.icon)}
          >
            {renderIcon?.()}
          </span>

          {label && (
            <Field.Label className={styles.label}>
              {label}
              {required && showRequired && (
                <span
                  aria-hidden="true"
                  className={styles.requiredIndicator}
                >
                  &nbsp;*
                </span>
              )}
            </Field.Label>
          )}
        </div>

        {fieldError}
      </Field.Root>
    );
  }

  return (
    <Field.Root
      className={cn('flex w-full flex-col gap-1', styles.root)}
      disabled={disabled}
      invalid={Boolean(error)}
    >
      <Select.Root
        items={options.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next as Value)}
        disabled={disabled}
        required={required}
        name={name}
        autoComplete={autoComplete}
        modal={false}
      >
        <div className="relative flex items-center">
          <Select.Trigger
            data-testid={dataTestId}
            className={cn('relative flex w-full items-center', styles.trigger)}
          >
            <Select.Value
              placeholder={placeholder}
              className={styles.value}
            />
            <Select.Icon className={styles.icon}>{renderIcon?.()}</Select.Icon>
          </Select.Trigger>

          {label && (
            <Field.Label className={styles.label}>
              {label}
              {required && showRequired && (
                <span
                  aria-hidden="true"
                  className={styles.requiredIndicator}
                >
                  &nbsp;*
                </span>
              )}
            </Field.Label>
          )}
        </div>

        <Select.Portal>
          <Select.Positioner
            sideOffset={4}
            alignItemWithTrigger={false}
            className="z-50"
          >
            <Select.Popup
              className={styles.popup}
              // The popup scrolls independently of item focus; make it keyboard-reachable
              // directly (axe: "Scrollable element focusable").
              tabIndex={0}
            >
              <Select.List>
                {options.map((option) => (
                  <Select.Item
                    key={String(option.value)}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.item}
                  >
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator className={styles.itemIndicator}>
                      {renderItemIndicator?.()}
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>

      {fieldError}
    </Field.Root>
  );
}
